import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiService } from '../services/api';

interface FubConnectionGuardProps {
  children: React.ReactNode;
}

const FubConnectionGuard: React.FC<FubConnectionGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: any) => state.auth);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [hasConnection, setHasConnection] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setCheckingConnection(false);
      return;
    }

    // Admin users bypass FUB connection requirement
    if (user?.role === 'ADMIN') {
      setHasConnection(true);
      setCheckingConnection(false);
      return;
    }

    (async () => {
      try {
        const connection = await apiService.getFubConnection();
        setHasConnection(connection.isConnected);

        if (!connection.isConnected) {
          navigate('/connect-fub');
          return;
        }
      } catch (error) {
        console.error('Failed to check FUB connection:', error);
        navigate('/connect-fub');
        return;
      } finally {
        setCheckingConnection(false);
      }
    })();
  }, [isAuthenticated, user, navigate]);

  if (checkingConnection) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {user?.role === 'ADMIN' ? 'Loading admin dashboard...' : 'Checking FUB connection...'}
          </p>
        </div>
      </div>
    );
  }

  // If user is not authenticated or doesn't have FUB connection, 
  // the navigation will be handled by the useEffect
  if (!hasConnection && isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default FubConnectionGuard;
